import React, {FormEvent, useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {Body, Button, Icon, Tooltip} from '@lightricks/react-design-system';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import ConfirmModal from '@/components/confirm-modal';
import useAssumeDelivery from '@/hooks/mutations/use-assume-delivery';
import useSendGiftCode from '@/hooks/mutations/use-send-gift-code';
import useShipShipment from '@/hooks/mutations/use-ship-shipment';
import type {
  CampaignCreatorRow,
  CampaignDeliverables,
} from '../../types/creatorTabTypes';
import styles from './DeliverableCodeCell.module.scss';

interface DeliverableCodeCellProps {
  creator: CampaignCreatorRow;
  deliverableType: CampaignDeliverables;
  onActionComplete: () => void;
}

function getTrackingNumberInputTooltip(): string {
  return [
    translate(
      'Do NOT include the carrier name, extra text, or extra space. The shipment will not track correctly.'
    ),
    translate('ex. 1Z85350362837659'),
  ].join('\n\n');
}

function DeliverableCodeCell({
  creator,
  deliverableType,
  onActionComplete,
}: DeliverableCodeCellProps) {
  const isGiftCode = deliverableType === 'gift_codes';
  const savedCode = isGiftCode ? creator.couponCode : creator.trackingCode;
  const [inputValue, setInputValue] = useState(savedCode ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [showAssumeDeliveryModal, setShowAssumeDeliveryModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const {mutate: shipShipment, isLoading: isShipping} = useShipShipment();
  const {mutate: sendGiftCode, isLoading: isSendingCoupon} = useSendGiftCode();
  const {mutate: assumeDelivery, isLoading: isAssumingDelivery} =
    useAssumeDelivery();

  useEffect(() => {
    setInputValue(savedCode ?? '');
    setIsEditing(false);
  }, [savedCode, creator.id]);

  useEffect(() => {
    // Only when entering edit on an *existing* saved code (the pill path) do we
    // select-all so the user can overwrite it. For a brand-new code there's
    // nothing to select, and selecting mid-typing would eat the first character.
    if (isEditing && !isGiftCode && savedCode) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing, isGiftCode]);

  const isFulfilled = creator.fulfillmentState === 'fulfilled';
  const isRunning = isShipping || isSendingCoupon || isAssumingDelivery;
  const canEdit = !isFulfilled && !isRunning && !!creator.deliverableId;
  const isShipmentReadOnly =
    !isGiftCode && !!savedCode && !isEditing && inputValue === savedCode;
  const isInputDisabled = !canEdit || isShipmentReadOnly;
  const hasInvalidTracking =
    !!creator.trackingCodeInvalidatedAt &&
    creator.fulfillmentState === 'en_route';
  const canAssumeDelivery =
    canEdit &&
    !isGiftCode &&
    creator.fulfillmentState === 'en_route' &&
    hasInvalidTracking;
  const showTrackViaCourierLink =
    !isGiftCode &&
    creator.fulfillmentState === 'en_route' &&
    !!creator.trackingCodeUrl &&
    !hasInvalidTracking;

  const isEditingShipment =
    !isGiftCode &&
    (isEditing ||
      (!savedCode ? !!inputValue.trim() : inputValue !== savedCode));
  // Click-the-pill-to-edit replaces the old separate "Edit" text button. When
  // a tracking code is saved and we're not already editing, the input renders
  // as a read-only pill that activates edit mode on click.
  const isClickToEditPill =
    canEdit && !isRunning && !isGiftCode && !!savedCode && !isEditingShipment;
  const showStackedActions =
    canEdit &&
    !isRunning &&
    (isGiftCode ? inputValue.trim() !== (savedCode ?? '') : isEditingShipment);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (!isGiftCode) {
      if (!savedCode && value.trim()) {
        setIsEditing(true);
      } else if (savedCode && value !== savedCode) {
        setIsEditing(true);
      }
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!creator.deliverableId || !inputValue.trim()) {
      return;
    }

    if (isGiftCode) {
      sendGiftCode(
        {giftCodeId: creator.deliverableId, couponCode: inputValue.trim()},
        {
          onSuccess: onActionComplete,
          onError: (error) =>
            raiseFlashMessageError({
              subtitle: extractBackendErrorDetail(error),
            }),
        }
      );
      return;
    }

    shipShipment(
      {
        shipmentId: creator.deliverableId,
        trackingCode: inputValue.trim(),
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          onActionComplete();
        },
        onError: (error) =>
          raiseFlashMessageError({
            subtitle: extractBackendErrorDetail(error),
          }),
      }
    );
  };

  const handleClear = () => {
    setInputValue('');
    if (!isGiftCode) {
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setInputValue(savedCode ?? '');
    setIsEditing(false);
  };

  const handleConfirmAssumeDelivery = () => {
    if (!creator.deliverableId) {
      return;
    }

    assumeDelivery(
      {shipmentId: creator.deliverableId},
      {
        onSuccess: () => {
          setShowAssumeDeliveryModal(false);
          onActionComplete();
        },
        onError: (error) =>
          raiseFlashMessageError({
            subtitle: extractBackendErrorDetail(error),
          }),
      }
    );
  };

  if (!creator.deliverableId) {
    return <Body size="md">-</Body>;
  }

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const placeholder = isGiftCode
    ? translate('Enter gift code')
    : translate('Enter tracking number');
  const showTrackingInputTooltip = !isGiftCode && !isFulfilled;

  const handlePillClick = () => {
    if (isClickToEditPill) {
      setIsEditing(true);
    }
  };

  const trackingInput = (
    <input
      ref={inputRef}
      type="text"
      className={`${styles.input} ${isClickToEditPill ? styles.inputClickable : ''}`}
      value={inputValue}
      disabled={isInputDisabled && !isClickToEditPill}
      readOnly={isClickToEditPill}
      onClick={handlePillClick}
      onChange={(event) => handleInputChange(event.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      title={isClickToEditPill ? translate('Click to edit') : undefined}
    />
  );

  let secondaryActionButton = null;
  if (isGiftCode) {
    secondaryActionButton = (
      <button type="button" className={styles.textButton} onClick={handleClear}>
        {translate('Clear')}
      </button>
    );
  } else if (savedCode) {
    secondaryActionButton = (
      <button
        type="button"
        className={styles.textButton}
        onClick={handleCancelEdit}
      >
        {translate('Cancel')}
      </button>
    );
  }

  return (
    <div
      className={styles.container}
      onClick={stopRowClick}
      onMouseDown={stopRowClick}
    >
      <form
        className={`${styles.form} ${showStackedActions ? styles.formExpanded : ''}`}
        onSubmit={handleSubmit}
      >
        {showTrackingInputTooltip ? (
          <Tooltip
            title={getTrackingNumberInputTooltip()}
            arrow
            placement="top"
          >
            <Box
              component="span"
              className={styles.inputTooltipTrigger}
              data-testid="tracking-number-input-tooltip-trigger"
            >
              {trackingInput}
            </Box>
          </Tooltip>
        ) : (
          trackingInput
        )}

        {isRunning ? (
          <span className={styles.statusText}>{translate('Saving...')}</span>
        ) : null}

        {showStackedActions ? (
          <div className={styles.actions}>
            {inputValue.trim() ? (
              <button type="submit" className={styles.saveButton}>
                {translate('Save')}
              </button>
            ) : null}
            {secondaryActionButton}
          </div>
        ) : null}
      </form>

      {hasInvalidTracking && creator.trackingCodeInvalidatedReason ? (
        <span className={styles.invalidReason}>
          {creator.trackingCodeInvalidatedReason}
        </span>
      ) : null}

      {showTrackViaCourierLink ? (
        <Button
          appearance="neutral"
          mode="plain"
          size="small"
          className={styles.trackButton}
          startIcon={
            <Icon
              name="Navigation-Web"
              size="small"
              appearance="neutral"
              color="currentColor"
            />
          }
          onClick={(event) => {
            stopRowClick(event);
            window.open(
              creator.trackingCodeUrl ?? '',
              '_blank',
              'noopener,noreferrer'
            );
          }}
        >
          {translate('Track')}
        </Button>
      ) : null}

      {canAssumeDelivery ? (
        <button
          type="button"
          className={styles.assumeDeliveryButton}
          onClick={() => setShowAssumeDeliveryModal(true)}
        >
          {translate('Assume delivery')}
        </button>
      ) : null}

      <ConfirmModal
        open={showAssumeDeliveryModal}
        title="Mark shipment as delivered?"
        body="This can't be undone. The creator will be notified and their content due date will be set immediately."
        confirmLabel="Mark as delivered"
        cancelLabel="Go Back"
        isLoading={isAssumingDelivery}
        onCancel={() => setShowAssumeDeliveryModal(false)}
        onConfirm={handleConfirmAssumeDelivery}
      />
    </div>
  );
}

export default DeliverableCodeCell;
